import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { loggedInUserState } from "redux/userSlice";

import {
  Box,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material"
import {Menu, Image} from "semantic-ui-react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {useParams, useLocation} from "react-router-dom";
import {createFileNameUnique} from "utils/formUtils";
import { filterRequestedSupplementalDocFromCertificate } from "utils/certUtils";

import { SubtitleInline } from "components/shared/Typography";
import { SideMenu } from "components/shared/SideMenuContainer";
import LoaderButton from "components/shared/LoaderButton";
import CertificateFileDisplay from "components/CertificateDetails/CertificateFileDisplay";
import CertificateFilesSection from "components/CertificateDetails/CertificateFilesSection";
import { getEmailFieldError } from "utils/validationUtils"
import { hasRequiredDocs } from "utils/certValidationUtils";
import cognito_groups from "constants/cognito_groups";
import { getVendorDocumentUploadTemplate } from "constants/client_properties";
import certificate_actions from "constants/certificate_actions";
import {ROUTE_FRAGMENTS} from "constants/routes"
import {get, post} from "aws-amplify/api";
import "./vendor-upload.scss"

import AmplitudeService, { AMPLITUDE_EVENT } from "services/amplitude";
import {uploadUsingPresignedUrl, getCertificateFiles} from "services/appResources";
import DisplayProjectCoverages from "components/shared/DisplayProjectCoverages";
import DisplayProjectDescriptionOfOperation from "components/shared/DisplayProjectDescriptionOfOperation";
import PrivacyPolicy from "./PrivacyPolicy";
import MultiAgentModal from "./MultiAgentModal";
import MultiAgentListModal from "./MultiAgentListModal";
import UploadRequestedCOISupplementalDocumentation from "./UploadRequestedCOISupplementalDocumentation";
import Tooltips from "components/shared/Tooltips";  // Add this import
import UploadProgressModal from '../VendorDetails/UploadProgressModal';
import { getCoverageFullName } from "utils/projectCoverageRequirementsUtils";
import { Certificatetype } from "models";

const menuItems = [
  {
    key: "coverageDetails",
    label: "Coverage Details",
  },
  {
    key: "requiredDocumentation",
    label: "Required Documentation",
  },
];



export default function VendorDocumentUpload () {

  const { isAdmin, loggedInUser } = useSelector(loggedInUserState);
  const location = useLocation();
  const { certificateId, vendorId, organizationId, viewMode } = useParams();
  const [initialLoading, setInitialLoading] = useState(false);
  const [additionalInformation, setAdditionalInformation] = useState("")
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [notify, setNotify] = useState()
  const [vendor, setVendor] = useState()

  const [isSelf, setIsSelf] = useState();
  const [uploadAgentName, setUploadAgentName] = useState(null)
  const [uploadAgentEmail, setUploadAgentEmail] = useState(null)
  const [uploadAgentEmailError, setUploadAgentEmailError] = useState(null)
  const [supplementalDocumentationUploadError, setSupplementalDocumentationUploadError] = useState(0)
  const [logo, setLogo] = useState()
  const [project, setProject] = useState()
  const [organization, setOrganization] = useState()
  const [certificateData, setCertificateData] = useState()
  const [certificateFiles, setCertificateFiles] = useState([])
  const [fileSelected, setFileSelected] = useState({ name: null })
  const [hasPending, setHasPending] = useState(false)
  const [isPrivacyPolicyConfirmed, setIsPrivacyPolicyConfirmed] = useState(false)
  const [isDocumentationModalCompleted, setIsDocumentationModalCompleted] = useState(false);
  const [showDocumentationStep2, setShowDocumentationStep2] = useState(false);
  const [initialCoverageRequirements, setInitialCoverageRequirements] = useState();
  const [uploadedSupplementalCOIDocumentation, setUploadedSupplementalCOIDocumentation] = useState({});
  const [uploadProgress, setUploadProgress] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [numberofFilesToUpload, setNumberofFilesToUpload] = useState(0);
  const [isMultiAgentModalOpen, setIsMultiAgentModalOpen] = useState(false);
  const [multiAgentData, setMultiAgentData] = useState([]);
  const [multiAgentStatus, setMultiAgentStatus] = useState({ 
    success: false, 
    message: null 
  });


  const initialCoverageRequirementsWithLabels = JSON.parse(initialCoverageRequirements?.coverages || "[]").map((coverage) => ({ type: coverage?.name || coverage?.insuranceType, label: getCoverageFullName(coverage?.insuranceType, coverage?.name) }));

  const updateFileProgress = (file, status, progress) => {
    setUploadProgress(prev => {
      const fileIndex = prev.findIndex(f => {
        if (file.isSupplementalCoiFile) {
          return f.fullPath === file.fullPath
        } else {
          return f.name === file.name
        }
      });
      if (fileIndex >= 0) {
        const newProgress = [...prev];
        newProgress[fileIndex] = { ...newProgress[fileIndex], status, progress };
        return newProgress;
      }
      return [...prev, { ...file, status, progress }];
    });
  };

  const activeItem = String(location.hash).replace("#/", "") || menuItems[0].key;
  const isViewOnly = viewMode?.toLowerCase() === ROUTE_FRAGMENTS.SHARED.toLowerCase()

  useEffect(() => {
    if (isPrivacyPolicyConfirmed || isViewOnly) {
      loadInitialData()
    }
  }, [isPrivacyPolicyConfirmed])

  useEffect(() => {
    if(certificateFiles?.length && certificateFiles[certificateFiles.length - 1]?.isNew) {
      setFileSelected({ name: certificateFiles[certificateFiles.length - 1].name })
    }
  }, [certificateFiles])

  async function loadInitialData() {
    setInitialLoading(true)
    const {body} = await get({
      apiName: "FileUploadApi",
      path: "/upload",
      options: {
        queryParams: {
          organizationId,
          certificateId,
          vendorId,
        }
      }
    }).response;
    const result = await body.json();
    result?.logo && setLogo(result.logo)
    result?.notify && setNotify(result.notify)
    result?.project && setProject(result.project)
    result?.organization && setOrganization(result.organization)
    if(result?.inactive) {
      setVendor({inactive: true})
    } else {
      result?.vendor && setVendor(result.vendor)

      let initialFileSelected;
      const certificate = result?.certificate
      setCertificateData({ ...certificate })
      if (certificate?.initialCoverageRequirements && typeof certificate.initialCoverageRequirements === "string") {
        const initialCoverageRequirements = JSON.parse(certificate.initialCoverageRequirements);
        const filteredInitialCoverageRequirements = {
          ...initialCoverageRequirements,
          ...(certificate?.coverageType?.length && {
            requestedSupplementalDocumentation: filterRequestedSupplementalDocFromCertificate(initialCoverageRequirements?.requestedSupplementalDocumentation, certificate)
          }),
        };

        setInitialCoverageRequirements(filteredInitialCoverageRequirements);
        if (filteredInitialCoverageRequirements?.requestedSupplementalDocumentation && Object.keys(filteredInitialCoverageRequirements?.requestedSupplementalDocumentation).length) {
          const hasSupplementalDocRequests = Object.keys(filteredInitialCoverageRequirements?.requestedSupplementalDocumentation).find(insuranceType => {
            return Array.isArray(filteredInitialCoverageRequirements?.requestedSupplementalDocumentation[insuranceType]) && filteredInitialCoverageRequirements?.requestedSupplementalDocumentation[insuranceType.length];
          });
          setSupplementalDocumentationUploadError(hasSupplementalDocRequests ? true : false);
        }
        setSupplementalDocumentationUploadError(Object.entries(filteredInitialCoverageRequirements?.requestedSupplementalDocumentation || {}).some(([key, value]) => value?.length) ? true : false)
      }

      if((certificate?.acord25File || certificate.files?.length) && isViewOnly) {
        setFileSelected({ name: certificate?.acord25File || certificate.files[0] })
        let files = [];
        (certificate?.acord25File) && files.push({name: certificate?.acord25File, isAcord25: true});
        (certificate.files?.length) && (files = files.concat(certificate.files));
        getCertificateFiles(certificate.id, files, result => setCertificateFiles(result))
      } else {
        setCertificateFiles([])
      }
      if(result?.hasPending) {
        setHasPending(true)
      }
      if(initialFileSelected) {
        setFileSelected(initialFileSelected)
      }
    }
    setInitialLoading(false)
  }

  const handleDocumentationYes = () => {
    setIsMultiAgentModalOpen(true);
    setIsDocumentationModalCompleted(true);
  };

  const handleDocumentationNo = () => {
    setIsDocumentationModalCompleted(true);
  };

  const handleMultiAgentSave = async (agentData) => {
    try {
      // Format the data for the API endpoint
      const payload = {
        certificateID: certificateId, // This is from the component state/params
        insuranceAgents: agentData.map(agent => ({
          name: agent.name,
          agentEmails: agent.agentEmails,
          requestedCoverages: Array.isArray(agent.requestedCoverages) 
            ? agent.requestedCoverages 
            : [agent.coverage] // Handle both array and single coverage
        })),
        uploadLinkOrigin: window.location.origin + `/vendor/upload/${organizationId}/${vendorId}`,
        vendorName: vendor?.name,
      };
      // Make the API call to the /create-multi-agent endpoint
      const response = await post({
        apiName: "FileUploadApi",
        path: "/create-multi-agent",
        options: {
          body: payload,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      }).response;

      // Process the response if needed
      const result = await response.body.json();
      console.log('Multi-agent submission successful:', result);
      
      setMultiAgentData(agentData);
      setIsMultiAgentModalOpen(false);
      // Set flag to show success message
      setMultiAgentStatus({
        success: true,
        message: 'Your multi-agent request has been successfully sent. Each of your insurance agents will receive email instructions to upload their certificates.'
      });
    } catch (error) {
      console.error('Error submitting multi-agent data:', error);
      // Set error status with message
      setMultiAgentStatus({
        success: false,
        message: `There was a problem sending your multi-agent request. Error: ${error.message || 'Unknown error'}`
      });

      setIsMultiAgentModalOpen(false);
    }
  };

  const handleMultiAgentClose = () => {
    setIsMultiAgentModalOpen(false);
    setIsDocumentationModalCompleted(false);

  };

  if (!isPrivacyPolicyConfirmed && !isViewOnly) {
    return <PrivacyPolicy onConfirm={()=>setIsPrivacyPolicyConfirmed(true)}/>
  }

  // Render the MultiAgentListModal independently so it can be shown even after documentation modal is completed
  if (isMultiAgentModalOpen && certificateData?.certificateType !== Certificatetype.AGENT) {
    return (
      <MultiAgentListModal
        open={true}
        onSave={handleMultiAgentSave}
        onClose={handleMultiAgentClose}
        coverages={initialCoverageRequirementsWithLabels}
      />
    );
  }

  // Only show the initial question modal if we haven't completed it yet
  if (
    isPrivacyPolicyConfirmed &&
    !isDocumentationModalCompleted &&
    !isViewOnly &&
    !(certificateData?.acord25File || (certificateData?.coverageCertificates && certificateData?.coverageCertificates?.length)) &&
    certificateData?.status === "REQUESTED" && 
    certificateData?.certificateType !== Certificatetype.AGENT
  ) {
    return (
      <MultiAgentModal
        open={true}
        onYes={handleDocumentationYes}
        onNo={handleDocumentationNo}
        coverages={initialCoverageRequirementsWithLabels}
        clientName={organization?.name}
        logoUrl={logo}
      />
    );
  }


  const handleEntitySelect = (e) => {
    setIsSelf(e.target.value)
    if(e.target.value) {
      setUploadAgentName(null)
      setUploadAgentEmail(null)
      setUploadAgentEmailError(null)
    }
  }

  const handleFileUpload = (e, newAcord25FileName, { insuranceType, documentType, isSupplementalCoiFile } = {}) => {
    const file =  e.target.files ? e.target.files[0] : e?.dataTransfer?.files[0]
    if(file) {
      const reader = new FileReader();
      reader.onload = function(event) {
        const fileName = String(file.name).replaceAll(" ", "")
        const fileNameUnique = newAcord25FileName ? fileName : createFileNameUnique(
          String(file.name).replaceAll(" ", ""),
          certificateFiles
            .filter(certificateFile => !certificateFile.isAcord25)
            .map(certificateFile => certificateFile.name)
        )
        if (!isSupplementalCoiFile) {
          const newFiles = certificateFiles.map(file => {
            if (newAcord25FileName && file.isAcord25) {
              if (!file.isNew) {
                return {
                  ...file,
                  isDeleted: true,
                  isAcord25: true
                }
              } else {
                return {
                  ...file,
                  isNew: false,
                  isAcord25: true,
                  isDeleted: true,
                }
              }
            }
            return { ...file }
          })

          setCertificateFiles([
            ...newFiles,
            {
              content: event.target.result,
              name: fileNameUnique,
              isNew: true,
              isAcord25: !!newAcord25FileName
            }
          ])
        } else {
          setUploadedSupplementalCOIDocumentation({
            ...uploadedSupplementalCOIDocumentation,
            [insuranceType]: {
              ...(uploadedSupplementalCOIDocumentation[insuranceType] || {}),
              [documentType]: {
                content: event.target.result,
                name: fileNameUnique,
                isNew: true,
              },
            },
          });
          setFileSelected({
            name: fileNameUnique,
            insuranceType: insuranceType,
            documentType: documentType,
            isSupplementalCoiFile: true,
          })
        }
      }
        reader.readAsDataURL(file)
    }
  }

  const onCancelSave = () => {
    setCertificateFiles(
      certificateFiles?.filter(file => !file.isNew).map(file => ({...file, isDeleted: false}))
    )
    setFileSelected({ name: certificateData.acord25File || certificateData.files?.[0] })
  }

  const onSaveFile = async () => {
    setSaving(true);
    setShowUploadModal(true);
    
    let acord25Uploaded = false;
    const newSuppFiles = certificateFiles?.filter(file => file.isNew && !file.isAcord25);
    const acord25File = certificateFiles?.find(file => file.isNew && file.isAcord25);
 
    const supplementalCoverageCoiFiles = Object.entries(uploadedSupplementalCOIDocumentation).flatMap(([insuranceType, documents]) =>
      Object.entries(documents).map(([documentType, fileData]) => ({
        fullPath: `SupplementalCoverageDocumentation/${insuranceType}/${documentType}/${fileData.name}`,
        content: fileData.content,
        isNew: fileData.isNew,
        insuranceType,
        documentType,
        name: fileData.name,
        isSupplementalCoiFile: true,
        status: 'PENDING',
        progress: 0 
      }))
    );
    setNumberofFilesToUpload(newSuppFiles.length + (acord25File ? 1 : 0) + supplementalCoverageCoiFiles.length);
    const uploadedToS3SupplementalDocs = {};

    let newCertId = certificateId

    // we want to presign and upload the file after creating a scan record if it is acord25
    if (acord25File) {
      const uploaderName = isSelf ? vendor?.name : uploadAgentName;
      const certAcord25UpdateResult = await post({
        apiName: "FileUploadApi", 
        path: "/upload",
        options: {
          body: {
            certificateId,
            fileName: acord25File.name,
            vendor: vendor?.name,
            uploaderName: uploaderName,
            uploaderEmail: isSelf ? vendor?.email : uploadAgentEmail,
            uploadLink: `${window.location.origin}${window.location.pathname}`,
            isAcord25: true,
            notify,
            ...(additionalInformation && { additionalInformation }),
            notifyClientAdmins: [
              ...new Set([
                ...organization?.organizationUsers?.items
                  ?.filter((user) => user.role !== cognito_groups.ClientViewer && user.status === "ACTIVE")
                  .map((user) => user.email),
                ...(Array.isArray(vendor?.additionalEmails) ? vendor?.additionalEmails : []),
              ]),
            ],
            action: certificate_actions.CLIENT_ADMIN_NOTIFICATION,
            email: getVendorDocumentUploadTemplate(organization?.name, vendor?.name, project?.name, uploaderName, isSelf).CertificateUploadNotification,
          },
          headers: {
            "Content-Type": "application/json",
          }
        }
      }).response;

      const certAcord25UpdateResultData = await certAcord25UpdateResult.body.json();

      if(certAcord25UpdateResultData.scanId && certAcord25UpdateResultData.certificateId) {
        AmplitudeService.logEvent(AMPLITUDE_EVENT.COI_FILE_UPLOADED.eventName,
          AMPLITUDE_EVENT.COI_FILE_UPLOADED.eventProperties({
            userEmail: loggedInUser?.email,
            isAdminUser: isAdmin,
            clientId: organizationId,
            clientName: organization?.name,
            isSelf: isSelf,
            uploaderEmail: isSelf ? vendor?.email : uploadAgentEmail,
          })
        )
        newCertId = certAcord25UpdateResultData.certificateId
        const presignedAcord25UrlResponse = await post({
          apiName: "FileUploadApi",
          path: "/upload/presign",
          options: {
            body: {
              files: [{ name: acord25File.name, isAcord25: acord25File.isAcord25 }],
              certificateId: newCertId,
              scanId: certAcord25UpdateResultData.scanId
            },
            headers: {
              'Content-Type': 'application/json',
            }
          }
        }).response;
        const presignedAcord25UrlResponseData = await presignedAcord25UrlResponse.body.json();

        if (presignedAcord25UrlResponseData?.presignedUrls?.length) {
          const { presignUrl } = presignedAcord25UrlResponseData.presignedUrls[0]
          updateFileProgress(acord25File, 'UPLOADING', 0);
          const acord25ploadResult = await uploadUsingPresignedUrl(
            presignUrl, 
            acord25File.content,
            (progress) => {
              updateFileProgress(acord25File, 'UPLOADING', progress);
            }
          );
          updateFileProgress(acord25File, 'COMPLETED', 100);
          acord25Uploaded = true
          console.log("acord25ploadResult", acord25ploadResult)
        } else {
          console.log("No presign url created for Acord25 file - " + acord25File.name)
        }
      }
    }

    // we want to upload the file first when it is supplemental before updating the certificate
    if(newSuppFiles.length) {
      const presignedUrlResponse = await post({
        apiName: "FileUploadApi",
        path: "/upload/presign",
        options: {
          body: {
            files: newSuppFiles?.map(file => ({ name: file.name, isAcord25: file.isAcord25 })),
            certificateId: newCertId,
            existingFiles: newCertId === certificateId ? certificateData.files : null
          },
          headers: {
            'Content-Type': 'application/json',
          }
        }
      }).response;
      const presignedUrlResponseData = await presignedUrlResponse.body.json();
      const presignedUrls = presignedUrlResponseData?.presignedUrls
      if(presignedUrls?.length) {
        await Promise.all((newSuppFiles || []).map(async updatedFile => {
          const presignUrlConfig = presignedUrls.find(presignUrl => presignUrl.fileName === updatedFile.name);
            if(presignUrlConfig) {
              updateFileProgress(updatedFile, 'UPLOADING', 0);
              const uploadResult = await uploadUsingPresignedUrl(
                presignUrlConfig.presignUrl, 
                updatedFile.content,
                (progress) => {
                  updateFileProgress(updatedFile, 'UPLOADING', progress);
                }
              );
              updateFileProgress(updatedFile, 'COMPLETED', 100);
              console.log("uploadResult", uploadResult)
              const certUpdateResult = await post({
                apiName: "FileUploadApi",
                path: "/upload",
                options: {
                  body: {
                    certificateId: newCertId,
                    fileName: updatedFile.isAcord25 ? updatedFile.name : presignUrlConfig?.fileNameUnique,
                    vendor: vendor?.name,
                    uploaderName: isSelf ? vendor?.name : uploadAgentName,
                    uploaderEmail: isSelf ? vendor?.email : uploadAgentEmail,
                    uploadLink: `${window.location.origin}${window.location.pathname}`,
                    isAcord25: false,
                    notify,
                  },
                  headers: {
                    'Content-Type' : 'application/json',
                  }
                }
              })
            } else {
              console.log("No presign url created for file - " + updatedFile.name)
            }
          return Promise.resolve()
        }))
      }
    }

    // upload the supplemental file
    if (supplementalCoverageCoiFiles.length) {
      // enable temporary access with presigned url
      const presignedUrlResponse = await post({
        apiName: "FileUploadApi",
        path: "/upload/presign",
        options: {
          body: {
            files: supplementalCoverageCoiFiles?.map(file => ({ name: file.fullPath, isAcord25: false, isSupplementalCoiFile: true })),
            certificateId: newCertId,
            existingFiles: newCertId === certificateId ? certificateData.files : null
          },
          headers: {
            'Content-Type': 'application/json',
          }
        }
      }).response;
      
      const presignedUrlResponseData = await presignedUrlResponse.body.json();
      const presignedUrls = presignedUrlResponseData?.presignedUrls
      if(presignedUrls?.length) {
        await Promise.all((supplementalCoverageCoiFiles || []).map(async updatedFile => {
          try {
            const presignUrlConfig = presignedUrls.find(presignUrl => presignUrl.fileName === updatedFile.fullPath);
            if (presignUrlConfig) {
              uploadedToS3SupplementalDocs[updatedFile.insuranceType] = uploadedToS3SupplementalDocs[updatedFile.insuranceType] || {};
              updateFileProgress(updatedFile, 'UPLOADING', 0);
              const uploadResult = await uploadUsingPresignedUrl(
                presignUrlConfig.presignUrl, 
                updatedFile.content,
                (progress) => {
                  updateFileProgress(updatedFile, 'UPLOADING', progress);
                }
              );

              uploadedToS3SupplementalDocs[updatedFile.insuranceType][updatedFile.documentType] = updatedFile.name;
              // Update final status
              updateFileProgress(updatedFile, 'COMPLETED', 100);
            } else {
              console.log("No presign url created for supplemental COI file - " + updatedFile.name)
            }
          } catch (error) {
            console.error(`Failed to upload file ${updatedFile.name}:`, error);
            updateFileProgress(updatedFile.name, 'ERROR', 0);
          }
          return Promise.resolve()
        }))
        
        // Upload certificate with uploaded supplemental COI Files
        const certUpdateResult = await post({
          apiName: "FileUploadApi",
          path: "/upload",
          options: {
            body: {
              certificateId: newCertId,
              vendor: vendor?.name,
              uploaderName: isSelf ? vendor?.name : uploadAgentName,
              uploaderEmail: isSelf ? vendor?.email : uploadAgentEmail,
              uploadLink: `${window.location.origin}${window.location.pathname}`,
              isAcord25: false,
              supplementalCoiFiles: JSON.stringify(uploadedToS3SupplementalDocs),
            },
            headers: {
              'Content-Type' : 'application/json',
            }
          }
        })
      }
    }

    // If we have multi-agent data, use it for different coverage uploads
    if (multiAgentData.length > 0) {
      // Logic for handling multiple agents for different coverages
      // This would be implemented based on how you want to process the multi-agent data
      console.log("Multi-agent data to process:", multiAgentData);
    }

    setSaving(false)
    if(acord25Uploaded) {
      setHasPending(true)
    }
    setSaved(true)
  }

  const renderDisabledUploadMessage = (messageTitle, messageType) => (
    <>
      <Container maxWidth="xl">
        <Menu fixed="top"
              style={{
                boxShadow: "none",
                backgroundColor: "#f3f4f7"
              }}
        >
          <Menu.Item style={{borderRight: "none"}}>
            <Image size="mini" src={window.location.origin + "/app-logo-h110.png"} />
          </Menu.Item>
        </Menu>
      </Container>
      <Container sx={{mt: "90px", pb: 3}} maxWidth="xl">
        <Box sx={{display: "flex", alignItems: "center", flexFlow: "column"}}>
          <Typography 
            variant="h5" 
            pl={2}
            color={messageType === "error" ? "error" : "inherit"}
            dangerouslySetInnerHTML={{ __html: messageTitle }}
          />
          {messageType !== "error" && (
            <Typography variant="h6" pl={2}>
              Please contact <b>"{organization?.name}"</b> for more information.
            </Typography>
          )}
          {messageType === "error" && (
            <Button 
              variant="contained" 
              color="primary" 
              sx={{ mt: 3 }}
              onClick={() => setMultiAgentStatus({ success: false, message: null })}
            >
              Try Again
            </Button>
          )}
          <Image style={{margin: '2em'}} size="medium" src={window.location.origin + "/app-logo-h110.png"} />
        </Box>
      </Container>
    </>
  );
  const renderActionButtons = () => {
    const enableSave = certificateFiles?.length && (!supplementalDocumentationUploadError && (isSelf || (uploadAgentEmail && uploadAgentName && !uploadAgentEmailError)))
    return (
      <Box sx={{ml: "auto", alignSelf: "center", float: "right"}}>
        <Button
          size="small"
          onClick={onCancelSave}
          disabled={!certificateFiles?.length}
        >Cancel</Button>
        <Tooltips.LightTooltip 
          title={!enableSave ?  <Typography variant="caption"> Please ensure all required information is supplied including documents on the Required Documentation tab in order to complete.</Typography> : ""}
          arrow
        >
          <span>
            <LoaderButton
              variant="contained"
              size="small"
              onClick={onSaveFile}
              disabled={!enableSave}
              loading={saving}
            >
              UPLOAD
            </LoaderButton>
          </span>
        </Tooltips.LightTooltip>
      </Box>
    )
  }

  const handleUploadAgentEmailChange = (e) => {
    setUploadAgentEmail(e.target.value)
    if(!e.target.value) {
      setUploadAgentEmailError("Email is Required.")
    } else if(getEmailFieldError(e.target)) {
      setUploadAgentEmailError("Email is not in valid format.")
    } else {
      setUploadAgentEmailError("")
    }
  }

  //Disable upload for uploaded files
  if (!initialLoading && (certificateData?.acord25File && certificateData?.status !== "REQUESTED") && !isViewOnly) {
    return renderDisabledUploadMessage(`This link is not valid anymore (already uploaded certificate file ${ certificateData?.acord25File}).`)
  }else if (!initialLoading && (certificateData?.certificateType === Certificatetype.MULTI_AGENT && certificateData?.status !== "REQUESTED")) {
    //Disable upload for requested MULTI_AGENT certificate
    return renderDisabledUploadMessage ('This multi-agent link is not valid.')
  }else if (!initialLoading && multiAgentStatus.message) {
    return renderDisabledUploadMessage(
      multiAgentStatus.message,
      !multiAgentStatus.success ? "error" : undefined
    );
  }

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: "blur(8px)"}}
        open={initialLoading}
      >
        <CircularProgress color="inherit"/>
      </Backdrop>
      <Container maxWidth="xl">
        <Menu fixed="top"
              style={{
                boxShadow: "none",
                backgroundColor: "#f3f4f7"
              }}
        >
          <Menu.Item style={{borderRight: "none",}}>
            <Image size="mini" src={window.location.origin + "/app-logo-h110.png"} />
          </Menu.Item>
        </Menu>
      </Container>
      <Container sx={{mt: "90px", pb: 3}} maxWidth="xl">
        {
          !initialLoading && !!vendor?.inactive &&
          <Box sx={{display: "flex", alignItems: "center"}}>
            <Typography variant="h5" as="span" pl={2}>
              Upload for this subcontractor is disabled.
            </Typography>
          </Box>
        }
        { !initialLoading && (hasPending || saved) &&
          <Box sx={{display: "flex", alignItems: "center",flexFlow: "column"}}>
            {/*<Loader active inline indeterminate size='small' />*/}
            <Typography variant="h5" as="span" pl={2}>
              Thank you for uploading! We have received your certificate file(s).
              {hasPending && ` You will receive a confirmation email shortly.`}
            </Typography>
            <Image style={{margin: '2em'}} size="medium" src={window.location.origin + "/app-logo-h110.png"} />
          </Box>
        }
        {
          !initialLoading && !vendor?.inactive && !hasPending && !saved &&
          <>
            <Box sx={{display: "flex", alignItems: "center",flexFlow: "column", width: "800px"}}>
              <Image style={{margin: '2em'}} size="medium" src={window.location.origin + "/app-logo-h110.png"} />
              <Typography fontSize={14} fontStyle="italic" textAlign="justify">
                This document is for authorized personnel only and must not be shared or copied. If you are not the intended recipient, please disregard this message. Unauthorized access or use is prohibited. Notify the sender if received in error.
              </Typography>
            </Box>
            <Box sx={{textAlign: "right"}}>
              <Link
                href={`${window.location.origin}/images/sample/acord25.png`}
                target={"_blank"}
              >
                View Sample Acord25 <OpenInNewIcon fontSize={"small"}/>
              </Link>
            </Box>
            <Stack direction="row" spacing={2} mt={3}>
              <Stack>
                <CertificateFileDisplay
                  certificateFiles={certificateFiles || []}
                  supplementalCoiFiles={{ ...uploadedSupplementalCOIDocumentation }}
                  fileSelected={{ ...fileSelected }}
                  handleFileUpload={handleFileUpload}
                  disableUpload={true}
                />
              </Stack>

              <Divider orientation={"vertical"} flexItem={true} />

              <Stack sx={{ flexGrow: 1 }}>
                <SideMenu
                  items={[...menuItems]}
                  vertical={false}
                  style={{ marginTop: 0, marginBottom: "2rem" }}
                  className={"major"}
                  activeItem={activeItem}
                />
                {
                  !isViewOnly && activeItem === menuItems[0].key &&
                  <>
                    <Typography variant="h5" mr={2}>
                      {vendor?.useEntityName ? vendor?.entityName : vendor?.name}
                      {
                        renderActionButtons()
                      }
                    </Typography>
                    {vendor?.address1 && (
                      <Typography variant="body1" mr={2}>
                        {vendor.address1}
                      </Typography>
                    )}
                    {vendor?.address2 && (
                      <Typography variant="body2" mr={2}>
                        {vendor.address2}
                      </Typography>
                    )}
                    {(vendor?.city || vendor?.state || vendor?.zip) && (
                      <Typography variant="body2" mr={2}>
                        {vendor.city && `${vendor.city} ${(vendor.state || vendor.zip) && ','} `}
                        {vendor.state && `${vendor.state} ${vendor.zip && ','} `}
                        {vendor.zip || ''}
                    
                      </Typography>
                    )}
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <SubtitleInline>
                        {`WHO IS UPLOADING`}
                      </SubtitleInline>
                      <FormControl fullWidth={true} sx={{ mt: 2 }}>
                        <InputLabel id="party-select-label">I am...(Required)</InputLabel>
                        <Select
                          labelId="party-select-label"
                          id="party-select-label"
                          value={isSelf}
                          label="I am...(Required)"
                          onChange={handleEntitySelect}
                        >
                          <MenuItem key={"party-self"} value={true}>{`Self - ${vendor?.name}`}</MenuItem>
                          <MenuItem key={"party-self"} value={false}>Insurance Agent</MenuItem>
                        </Select>
                      </FormControl>
                      {
                        isSelf === false &&
                        <>
                          <TextField
                            fullWidth
                            label="Name (Required)"
                            sx={{ mt: 2 }}
                            value={uploadAgentName}
                            onChange={(e) => setUploadAgentName(e.target.value)}
                            onBlur={(e) => setUploadAgentName(e.target.value)}
                            error={uploadAgentName === ""}
                            helperText={uploadAgentName === "" ? "Name is Required." : ""}
                          />
                          <TextField
                            fullWidth
                            label="Contact Email (Required)"
                            sx={{ mt: 2 }}
                            value={uploadAgentEmail}
                            onChange={handleUploadAgentEmailChange}
                            onBlur={handleUploadAgentEmailChange}
                            inputProps={{ pattern: "^\\S+@\\S+\\.\\S+$" }}
                            error={!!uploadAgentEmailError}
                            helperText={uploadAgentEmailError}
                          />
                        </>
                      }
                    </Box>


                    <Stack display="flex" sx={{ mt: 2 }} justifyContent="space-between" flexDirection="row">
                      <TextField
                        fullWidth
                        multiline
                        rows="4"
                        placeholder={"Provide additional information ..."}
                        name="comments"
                        value={additionalInformation}
                        onChange={event => setAdditionalInformation(event.target.value)}
                      />
                    </Stack>

                  </>
                }
                {initialCoverageRequirements && activeItem === menuItems[0].key && (
                  <>
                  <DisplayProjectCoverages
                    project={project}
                    certificateHolder={organization?.name}
                      initialCoverageRequirements={initialCoverageRequirements}
                      displayCoverages={certificateData?.coverageType || []}
                    logo={logo}
                    />
                    <DisplayProjectDescriptionOfOperation descriptionOfOperation={certificateData?.initialDescriptionOfOperation} />
                  </>
                  )}
                {!isViewOnly && activeItem === menuItems[1]?.key &&
                  <>
                  <Typography variant="h5" mr={2} mb={2}>
                  {
                    renderActionButtons()
                  }
                  </Typography>
                  <Divider sx={{mt: 2, mb: 1}}/>
                  <CertificateFilesSection
                      {...{
                        acord25FileName: certificateData?.acord25File,
                        certificateFiles: certificateFiles,
                        handleFileUpload,
                        setFileSelected,
                        disableUpload: hasPending || saving,
                        saving,
                      }}
                    >
                      {hasRequiredDocs(initialCoverageRequirements) && (
                        <UploadRequestedCOISupplementalDocumentation
                          fileSelected={fileSelected}
                          uploadedSupplementalCOIDocumentation={uploadedSupplementalCOIDocumentation}
                          requestedSupplementalDocumentation={initialCoverageRequirements?.requestedSupplementalDocumentation}
                          setFileSelected={setFileSelected}
                          onError={setSupplementalDocumentationUploadError}
                          handleFileUpload={handleFileUpload}
                        />
                      )}
                  </CertificateFilesSection>
                  <Box sx={{ mt: 2, mb: 2 }}></Box>

                  
                  </>}
              </Stack>
            </Stack>
          </>
        }
      </Container>
      <UploadProgressModal 
        open={showUploadModal} 
        files={uploadProgress}
        onClose={() => setShowUploadModal(false)}
        numberOfFilesToUpload={numberofFilesToUpload}
      />
    </>
  )
}
